
import DragDrop from '@/components/input/DragDrop.vue'
import { DocumentUploadState } from '@/components/modals/UploadDocumentsDialog.types'
import { TeamLibraryConst } from '@/constants/teamlibrary'
import IconCheckCircleFilled from '@svg/IconCheckCircleFilled.svg'
import IconClose from '@svg/IconClose.svg'
import IconCloseFilled from '@svg/IconCloseFilled.svg'
import IconInfoFilled from '@svg/IconInfoFilled.svg'
import IconUploadFile from '@svg/IconUploadFile.svg'
import { Component, Vue } from 'vue-property-decorator'

interface DocumentFileProgress {
  file: File
  state: DocumentUploadState
  errorMsg: string
}

@Component({
  components: {
    DragDrop,
    IconCheckCircleFilled,
    IconClose,
    IconCloseFilled,
    IconInfoFilled,
    IconUploadFile,
  },
})
export default class UploadDocumentsDialog extends Vue {
  public $refs!: Vue['$refs'] & {
    dragdrop: DragDrop
  }

  private dialogVisible: boolean = false
  private selectedFiles: File[] = []
  private acceptedFileFormats: string[] = ['.pdf', '.txt']
  private maxFileSizeMB: number = TeamLibraryConst.ReferenceFileMaxSizeMB
  private maxFiles: number = 0
  private isUploading: boolean = false
  private uploadingFiles: DocumentFileProgress[] = []

  private get canImport(): boolean {
    return this.selectedFiles.length > 0
  }

  private get uploadStates() {
    return DocumentUploadState
  }

  private get hasUploadFinished(): boolean {
    if (this.uploadingFiles.length === 0) {
      return false
    }
    return this.uploadingFiles.every((item) => item.state === DocumentUploadState.DONE || item.state === DocumentUploadState.ERROR)
  }

  public show(options?: { maxFiles?: number }) {
    this.dialogVisible = true
    this.maxFiles = options && options.maxFiles && options.maxFiles > 0 ? options.maxFiles : 0
    this.resetSelectionState()
    this.isUploading = false
    this.uploadingFiles = []
  }

  public hide() {
    this.dialogVisible = false
    this.isUploading = false
    this.uploadingFiles = []
    this.resetSelectionState()
  }

  public updateFileState(index: number, state: DocumentUploadState, errorMsg?: string) {
    if (index >= 0 && index < this.uploadingFiles.length) {
      this.$set(this.uploadingFiles, index, {
        ...this.uploadingFiles[index],
        state,
        errorMsg: errorMsg || this.uploadingFiles[index].errorMsg,
      })
    }
  }

  public setUploading(value: boolean) {
    this.isUploading = value
  }

  public markPendingAsError(errorMsg: string) {
    for (let index = 0; index < this.uploadingFiles.length; index++) {
      const state = this.uploadingFiles[index].state
      if (state === DocumentUploadState.WAITING || state === DocumentUploadState.UPLOADING) {
        this.updateFileState(index, DocumentUploadState.ERROR, errorMsg)
      }
    }
  }

  private stateIcon(state: DocumentUploadState): string {
    switch (state) {
      case DocumentUploadState.DONE:
        return 'IconCheckCircleFilled'
      case DocumentUploadState.ERROR:
        return 'IconCloseFilled'
      default:
        return 'IconUploadFile'
    }
  }

  private stateIconClass(state: DocumentUploadState): string {
    switch (state) {
      case DocumentUploadState.DONE:
        return 'upload-progress__icon--success'
      case DocumentUploadState.ERROR:
        return 'upload-progress__icon--error'
      default:
        return 'upload-progress__icon--default'
    }
  }

  private stateLabel(state: DocumentUploadState): string {
    switch (state) {
      case DocumentUploadState.WAITING:
        return '(Waiting)'
      case DocumentUploadState.UPLOADING:
        return '(Uploading)'
      case DocumentUploadState.DONE:
        return '(Done)'
      case DocumentUploadState.ERROR:
        return '(Error)'
      default:
        return ''
    }
  }

  private onFilesSelected(files: File[]) {
    this.selectedFiles = files
  }

  private resetSelectionState() {
    this.selectedFiles = []
    this.$nextTick(() => {
      if (this.$refs.dragdrop) {
        this.$refs.dragdrop.clearFiles()
      }
    })
  }

  private onDismissClick() {
    if (this.isUploading) {
      return
    }

    if (this.hasUploadFinished) {
      this.onCloseClick()
      return
    }

    this.hide()
    this.$emit('canceled')
  }

  private onCancelClick() {
    this.onDismissClick()
  }

  private async onImportClick() {
    this.uploadingFiles = this.selectedFiles.map((file) => ({
      file,
      state: DocumentUploadState.WAITING,
      errorMsg: '',
    }))
    this.isUploading = true

    // Ensure the progress UI is rendered before parent upload work begins.
    await this.$nextTick()
    this.$emit('import', [...this.selectedFiles])
  }

  private onCloseClick() {
    const hasUploadedItems = this.uploadingFiles.length > 0
    this.hide()
    // Always notify parent to refresh list after upload attempts,
    // including partial success (some done, some failed).
    if (hasUploadedItems) {
      this.$emit('upload-complete')
    }
  }
}
